import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom/dist';
import AddBrand from '../../core/modals/inventory/addbrand';
import EditBrand from '../../core/modals/inventory/editbrand';
import { userActions } from "../../core/action/userAction";
import Swal from 'sweetalert2';
import Table from '../../core/pagination/datatable'
import Select from 'react-select';
import Sliders from 'feather-icons-react/build/IconComponents/Sliders';
import { ChevronUp, Filter, PlusCircle, RotateCcw, StopCircle, Calendar } from 'feather-icons-react/build/IconComponents';
import { DatePicker } from 'antd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ImageWithBasePath from '../../core/img/imagewithbasebath';
import { setToogleHeader } from '../../core/redux/action';
import { brandActions } from "../../core/action/brandaction";
import moment from 'moment';
import { Eye } from 'feather-icons-react/build/IconComponents'; // Import Eye icon for view

const BrandList = () => {
    const dataSource = useSelector((state) => state.brand_list || []);
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State for filters
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        date: ''
    });
    const [selectedDate, setSelectedDate] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    const [sortConfig, setSortConfig] = useState({ value: 'newest', label: 'Newest' });

    // Filter brands client-side based on search term
    const getFilteredBrands = React.useMemo(() => {
        if (!searchTerm || !dataSource || dataSource.length === 0) {
            return dataSource;
        }

        const searchLower = searchTerm.toLowerCase().trim();

        return dataSource.filter(brand => {
            const name = (brand.brand_name || brand.name || '').toLowerCase();
            const description = (brand.description || '').toLowerCase();

            // Exact name match has highest priority
            if (name === searchLower) return true;

            // Name starts with search term
            if (name.startsWith(searchLower)) return true;

            // Description contains search term
            if (description.includes(searchLower)) return true;

            return false;
        });
    }, [searchTerm, dataSource]);
    
    // State for view modal
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState(null);

    const oldandlatestvalue = [
        { value: "date", label: "Sort by Date" },
        { value: "newest", label: "Newest" },
        { value: "oldest", label: "Oldest" },
    ];

    const statusOptions = [
        { value: "", label: "Choose Status" },
        { value: "Active", label: "Active" },
        { value: "InActive", label: "InActive" },
    ];

    // Check permissions - Use the permission system
    const canCreate = userActions.hasPermission('create-brands');
    const canEdit = userActions.hasPermission('edit-brands');
    const canDelete = userActions.hasPermission('delete-brands');
    const canView = userActions.hasPermission('view-brands');
    
    // Get user role for super admin check
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.role_slug === 'super-admin';

    // Fetch brands with useCallback
    const fetchBrands = useCallback(async () => {
        // Check if user has view permission
        if (!canView && !isSuperAdmin) {
            console.warn('User does not have permission to view brands');
            return;
        }
        
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                search: searchTerm,
                status: filters.status,
                start_date: filters.date,
                end_date: filters.date,
                sort_by: sortConfig.value === 'date' ? 'created_at' : 'created_at',
                sort_order: sortConfig.value === 'oldest' ? 'ASC' : 'DESC'
            };

            const result = await dispatch(brandActions.fetchBrands(params));
            
            if (result.success && result.data.pagination) {
                setPagination(prev => ({
                    ...prev,
                    total: result.data.pagination.total_items || 0,
                    totalPages: result.data.pagination.total_pages || 1
                }));
            }
        } catch (error) {
            console.error('Error fetching brands:', error);
        } finally {
            setLoading(false);
        }
    }, [dispatch, pagination.page, pagination.limit, searchTerm, filters, sortConfig, canView, isSuperAdmin]);

    // Handle view brand
    const handleViewBrand = (record) => {
        setSelectedBrand(record);
        setShowViewModal(true);
    };

    // Handle edit brand
    const handleEditBrand = (record) => {
        if (!canEdit && !isSuperAdmin) {
            Swal.fire({
                icon: 'error',
                title: 'Access Denied',
                text: 'You do not have permission to edit brands',
                timer: 3000
            });
            return;
        }
        dispatch({ type: "selected_brand", payload: record });
    };

    // Handle delete brand
    const handleDeleteBrand = (record) => {
        if (!canDelete && !isSuperAdmin) {
            Swal.fire({
                icon: 'error',
                title: 'Access Denied',
                text: 'You do not have permission to delete brands',
                timer: 3000
            });
            return;
        }
        
        Swal.fire({
            title: 'Are you sure?',
            text: `Delete brand "${record.Brand}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                const response = await dispatch(brandActions.deleteBrand(record.id));
                if (response.success) {
                    fetchBrands();
                }
            }
        });
    };

    // Handle add new brand
    const handleAddBrand = () => {
        if (!canCreate && !isSuperAdmin) {
            Swal.fire({
                icon: 'error',
                title: 'Access Denied',
                text: 'You do not have permission to create brands',
                timer: 3000
            });
            return;
        }
        // Trigger the add brand modal
        document.querySelector('[data-bs-target="#add-brand"]')?.click();
    };

    // Handle search
    const handleSearch = (e) => {
        if (e.key === 'Enter' || e.type === 'click') {
            setPagination(prev => ({ ...prev, page: 1 }));
            fetchBrands();
        }
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    // Handle date change
    const handleDateChange = (date) => {
        setSelectedDate(date);
        if (date) {
            handleFilterChange('date', moment(date).format('YYYY-MM-DD'));
        } else {
            handleFilterChange('date', '');
        }
    };

    // Apply filters
    const handleApplyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchBrands();
        setIsFilterVisible(false);
    };

    // Clear filters
    const handleClearFilters = () => {
        setFilters({
            status: '',
            date: ''
        });
        setSelectedDate(null);
        setSearchTerm('');
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchBrands();
    };

    // Handle sort change
    const handleSortChange = (option) => {
        setSortConfig(option);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Handle refresh
    const handleRefresh = () => {
        fetchBrands();
    };

    // Handle page change
    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
    };

    // Handle limit change
    const handleLimitChange = (e) => {
        setPagination(prev => ({ 
            ...prev, 
            limit: parseInt(e.target.value), 
            page: 1 
        }));
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            sorter: true,
        },
        {
            title: "Brand",
            dataIndex: "Brand",
            sorter: true,
        },
        {
            title: "Brand Slug",
            dataIndex: "BrandSlug",
            sorter: true,
        },
        {
            title: "Created On",
            dataIndex: "CreateDate",
            sorter: true,
            render: (text) => text ? new Date(text).toLocaleDateString() : '-'
        },
        {
            title: "Status",
            dataIndex: "Status",
            render: (text) => (
                <span className={`badge ${text === 'Active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
                    {text}
                </span>
            ),
            sorter: true,
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            key: 'actions',
            render: (_text, record) => (
                <div className="edit-delete-action">
                    {/* View Button - Always show if user has view permission */}
                    {(canView || isSuperAdmin) && (
                        <button 
                            className="btn btn-sm btn-outline-info me-2 p-2" 
                            onClick={() => handleViewBrand(record)}
                            title="View Details"
                        >
                            <Eye size={14} />
                        </button>
                    )}
                    
                    {/* Edit Button - Only show if user has edit permission */}
                    {(canEdit || isSuperAdmin) && (
                        <Link 
                            className="me-2 p-2" 
                            to="#" 
                            data-bs-toggle="modal" 
                            data-bs-target="#edit-brand"
                            onClick={() => handleEditBrand(record)}
                            title="Edit Brand"
                        >
                            <i data-feather="edit" className="feather-edit"></i>
                        </Link>
                    )}
                    
                    {/* Delete Button - Only show if user has delete permission */}
                    {(canDelete || isSuperAdmin) && (
                        <Link 
                            className="confirm-text p-2" 
                            to="#" 
                            onClick={() => handleDeleteBrand(record)}
                            title="Delete Brand"
                        >
                            <i data-feather="trash-2" className="feather-trash-2"></i>
                        </Link>
                    )}
                </div>
            )
        }
    ];

    // Tooltip functions
    const renderTooltip = (props) => (
        <Tooltip id="pdf-tooltip" {...props}>
            Pdf
        </Tooltip>
    );
    const renderExcelTooltip = (props) => (
        <Tooltip id="excel-tooltip" {...props}>
            Excel
        </Tooltip>
    );
    const renderPrinterTooltip = (props) => (
        <Tooltip id="printer-tooltip" {...props}>
            Printer
        </Tooltip>
    );
    const renderRefreshTooltip = (props) => (
        <Tooltip id="refresh-tooltip" {...props}>
            Refresh
        </Tooltip>
    );
    const renderCollapseTooltip = (props) => (
        <Tooltip id="refresh-tooltip" {...props}>
            Collapse
        </Tooltip>
    );

    // Initial load
    useEffect(() => {
        if (canView || isSuperAdmin) {
            fetchBrands();
        }
    }, [fetchBrands, canView, isSuperAdmin]);

    // Update when sort, page, or limit changes
    useEffect(() => {
        if (canView || isSuperAdmin) {
            fetchBrands();
        }
    }, [sortConfig, pagination.page, pagination.limit]);

    return (
        <div>
            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header">
                        <div className="add-item d-flex">
                            <div className="page-title">
                                <h4>Brand List</h4>
                                <h6>Manage your brands</h6>
                            </div>
                        </div>
                        <ul className="table-top-head">
                            <li>
                                <OverlayTrigger placement="top" overlay={renderTooltip}>
                                    <Link>
                                        <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                            <li>
                                <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                                    <Link data-bs-toggle="tooltip" data-bs-placement="top">
                                        <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                            <li>
                                <OverlayTrigger placement="top" overlay={renderPrinterTooltip}>
                                    <Link data-bs-toggle="tooltip" data-bs-placement="top">
                                        <i data-feather="printer" className="feather-printer" />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                            <li>
                                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                                    <Link 
                                        data-bs-toggle="tooltip" 
                                        data-bs-placement="top" 
                                        onClick={handleRefresh}
                                    >
                                        <RotateCcw />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                            <li>
                                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                                    <Link
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="top"
                                        id="collapse-header"
                                        className={data ? "active" : ""}
                                        onClick={() => { dispatch(setToogleHeader(!data)) }}
                                    >
                                        <ChevronUp />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                        </ul>
                        
                        {/* Add New Brand Button - Only show if user has create permission */}
                        {(canCreate || isSuperAdmin) && (
                            <div className="page-btn">
                                <button
                                    className="btn btn-added"
                                    onClick={handleAddBrand}
                                    data-bs-toggle="modal"
                                    data-bs-target="#add-brand"
                                >
                                    <PlusCircle className="me-2" />
                                    Add New Brand
                                </button>
                            </div>
                        )}
                    </div>
                    
                    <div className="card table-list-card">
                        <div className="card-body">
                            {/* Only show filters and search if user has view permission */}
                            {(canView || isSuperAdmin) && (
                                <>
                                    <div className="table-top">
                                        <div className="search-set">
                                            <div className="search-input">
                                                <input
                                                    type="text"
                                                    placeholder="Search by brand name or slug"
                                                    className="form-control form-control-sm formsearch"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                                                />
                                                <Link 
                                                    to="#" 
                                                    className="btn btn-searchset"
                                                    onClick={handleSearch}
                                                >
                                                    <i data-feather="search" className="feather-search" />
                                                </Link>
                                            </div>
                                        </div>
                                        <div className="search-path">
                                            <Link 
                                                className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`}
                                                onClick={() => setIsFilterVisible(!isFilterVisible)}
                                            >
                                                <Filter className="filter-icon" />
                                                <span>
                                                    <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                                                </span>
                                            </Link>
                                        </div>
                                        <div className="form-sort">
                                            <Sliders className="info-img" />
                                            <Select
                                                className="select"
                                                options={oldandlatestvalue}
                                                value={sortConfig}
                                                onChange={handleSortChange}
                                                placeholder="Newest"
                                            />
                                        </div>
                                    </div>

                                    {/* Filter Section */}
                                    <div
                                        className={`card${isFilterVisible ? " visible" : ""}`}
                                        id="filter_inputs"
                                        style={{ display: isFilterVisible ? "block" : "none" }}
                                    >
                                        <div className="card-body pb-0">
                                            <div className="row">
                                                <div className="col-lg-3 col-sm-6 col-12">
                                                    <div className="input-blocks">
                                                        <StopCircle className="info-img" />
                                                        <Select
                                                            className="select"
                                                            options={statusOptions}
                                                            placeholder="Choose Status"
                                                            value={statusOptions.find(opt => opt.value === filters.status)}
                                                            onChange={(selected) => handleFilterChange('status', selected?.value || '')}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-3 col-sm-6 col-12">
                                                    <div className="input-blocks">
                                                        <Calendar className="info-img" />
                                                        <div className="input-groupicon">
                                                            <DatePicker
                                                                selected={selectedDate}
                                                                onChange={handleDateChange}
                                                                className="filterdatepicker"
                                                                dateFormat="dd-MM-yyyy"
                                                                placeholder="Choose Date"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-lg-3 col-sm-6 col-12 ms-auto">
                                                    <div className="input-blocks d-flex gap-2">
                                                        <button 
                                                            className="btn btn-filters"
                                                            onClick={handleApplyFilters}
                                                        >
                                                            <i data-feather="search" className="feather-search" /> Search
                                                        </button>
                                                        <button 
                                                            className="btn btn-secondary"
                                                            onClick={handleClearFilters}
                                                        >
                                                            Clear
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Table */}
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading brands...</p>
                                    </div>
                                ) : !canView && !isSuperAdmin ? (
                                    <div className="text-center py-5">
                                        <i data-feather="lock" className="feather-lock" style={{ fontSize: '48px', opacity: 0.5 }} />
                                        <h5 className="mt-3">Access Denied</h5>
                                        <p className="text-muted">You dont have permission to view brands</p>
                                    </div>
                                ) : getFilteredBrands.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i data-feather="tag" className="feather-tag" style={{ fontSize: '48px', opacity: 0.5 }} />
                                        <h5 className="mt-3">No brands found</h5>
                                        <p className="text-muted">
                                            {searchTerm || Object.values(filters).some(v => v)
                                                ? "Try adjusting your search or filters"
                                                : "Add your first brand to get started"}
                                        </p>
                                        {(canCreate || isSuperAdmin) && (
                                            <button
                                                className="btn btn-primary mt-3"
                                                onClick={handleAddBrand}
                                                data-bs-toggle="modal"
                                                data-bs-target="#add-brand"
                                            >
                                                <PlusCircle size={16} className="me-2" />
                                                Add New Brand
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <Table columns={columns} dataSource={getFilteredBrands} />
                                        
                                        {/* Pagination */}
                                        {pagination.totalPages > 1 && (
                                            <div className="pagination-wrapper mt-3">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="pagination-info">
                                                        Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                                        {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                                    </div>
                                                    <div className="pagination-controls">
                                                        <select 
                                                            className="form-select form-select-sm me-2"
                                                            value={pagination.limit}
                                                            onChange={handleLimitChange}
                                                            style={{ width: 'auto' }}
                                                        >
                                                            <option value="10">10 per page</option>
                                                            <option value="20">20 per page</option>
                                                            <option value="50">50 per page</option>
                                                            <option value="100">100 per page</option>
                                                        </select>
                                                        
                                                        <nav>
                                                            <ul className="pagination pagination-sm mb-0">
                                                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                                    <button 
                                                                        className="page-link" 
                                                                        onClick={() => handlePageChange(pagination.page - 1)}
                                                                        disabled={pagination.page === 1}
                                                                    >
                                                                        Previous
                                                                    </button>
                                                                </li>
                                                                
                                                                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                                                    let pageNum;
                                                                    if (pagination.totalPages <= 5) {
                                                                        pageNum = i + 1;
                                                                    } else if (pagination.page <= 3) {
                                                                        pageNum = i + 1;
                                                                    } else if (pagination.page >= pagination.totalPages - 2) {
                                                                        pageNum = pagination.totalPages - 4 + i;
                                                                    } else {
                                                                        pageNum = pagination.page - 2 + i;
                                                                    }
                                                                    
                                                                    return (
                                                                        <li 
                                                                            key={pageNum} 
                                                                            className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}
                                                                        >
                                                                            <button 
                                                                                className="page-link" 
                                                                                onClick={() => handlePageChange(pageNum)}
                                                                            >
                                                                                {pageNum}
                                                                            </button>
                                                                        </li>
                                                                    );
                                                                })}
                                                                
                                                                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                                    <button 
                                                                        className="page-link" 
                                                                        onClick={() => handlePageChange(pagination.page + 1)}
                                                                        disabled={pagination.page === pagination.totalPages}
                                                                    >
                                                                        Next
                                                                    </button>
                                                                </li>
                                                            </ul>
                                                        </nav>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Modals */}
            <AddBrand onSuccess={fetchBrands} />
            <EditBrand onSuccess={fetchBrands} />
            
            {/* View Brand Modal */}
            {showViewModal && selectedBrand && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowViewModal(false)}>
                    <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Brand Details</h5>
                                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">ID:</label>
                                    <p>{selectedBrand.id}</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Brand Name:</label>
                                    <p>{selectedBrand.Brand}</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Brand Slug:</label>
                                    <p>{selectedBrand.BrandSlug}</p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Status:</label>
                                    <p>
                                        <span className={`badge ${selectedBrand.Status === 'Active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
                                            {selectedBrand.Status}
                                        </span>
                                    </p>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Created On:</label>
                                    <p>{selectedBrand.CreateDate ? new Date(selectedBrand.CreateDate).toLocaleString() : '-'}</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default BrandList;